/**
 * 서식 툴바 (P2-③, 확장) — 텍스트 선택 시 뜨는 팝업 툴바.
 * 원본 CustomFormattingToolbar 패리티: 블록 타입 · B/I/U/S/code · 정렬 · 글자크기 · 색상 · 들여쓰기 · 링크.
 *  - 일반 블록(.content) 선택: 전체 도구 노출.
 *  - 표 셀(td.lumir-cell) 선택: 인라인 도구(마크/크기/색)만 — 블록 도구는 표 전용 툴바가 담당.
 * inline-format 엔진(applyInlineMark/readActiveMarks) 활용. mousedown preventDefault로 선택 유지.
 */

import { applyInlineMark, readActiveMarks } from "../inline-format.js";
import { setBlockType, setBlockAlignment, indentBlock, outdentBlock } from "../editing.js";
import { directContent } from "../serialize/block.js";
import { BLOCK_TYPE_DEFS } from "../shared/block-types.js";
import { TEXT_PALETTE, BG_PALETTE } from "../shared/palettes.js";
import { ICON } from "./icons.js";

const MARK_BTNS = [
  { mark: "bold", icon: ICON.bold, title: "굵게" },
  { mark: "italic", icon: ICON.italic, title: "기울임" },
  { mark: "underline", icon: ICON.underline, title: "밑줄" },
  { mark: "strike", icon: ICON.strikethrough, title: "취소선" },
  { mark: "code", icon: ICON.code, title: "코드" },
];
const ALIGN_BTNS = [
  { align: "left", icon: ICON.alignLeft, title: "왼쪽 정렬" },
  { align: "center", icon: ICON.alignCenter, title: "가운데 정렬" },
  { align: "right", icon: ICON.alignRight, title: "오른쪽 정렬" },
];
const FS_PRESETS = [10, 12, 14, 16, 18, 20, 24, 28];
const FS_MIN = 8, FS_MAX = 96, FS_DEFAULT = 14;

export function attachFormatToolbar(editorEl, opts) {
  opts = opts || {};
  const isComposing = typeof opts.isComposing === "function" ? opts.isComposing : () => false;
  const onChange = typeof opts.onChange === "function" ? opts.onChange : () => {};
  const suppress = typeof opts.suppress === "function" ? opts.suppress : () => false;
  const onLink = typeof opts.onLink === "function" ? opts.onLink : null;
  const cross = opts.cross || null; // 크로스블록 선택 서식 파사드(있으면 다중 블록 라우팅)
  const isCross = () => !!(cross && cross.isActive());
  const headingLevels = Array.isArray(opts.headingLevels) && opts.headingLevels.length ? opts.headingLevels : [1, 2, 3, 4, 5, 6];
  const BT = (opts.locale && opts.locale.blockTypes) || {};
  const blockTypeDefs = BLOCK_TYPE_DEFS.filter(([v]) => { const m = v.match(/^heading:(\d)/); return !m || headingLevels.includes(+m[1]); });

  const bar = document.createElement("div");
  bar.className = "lumir-format-toolbar";
  bar.style.position = "absolute";
  bar.style.display = "none";
  bar.innerHTML = buildHtml();
  document.body.appendChild(bar);

  let activeEditable = null;
  let savedRange = null; // 툴바 입력/셀렉트 포커스 시 잃는 에디터 선택을 보존

  function activeBlock() {
    return activeEditable && activeEditable.closest ? activeEditable.closest(".block") : null;
  }
  function captureRange() {
    const s = window.getSelection();
    if (!s || !s.rangeCount || !activeEditable) return;
    const r = s.getRangeAt(0);
    if (activeEditable.contains(r.startContainer) && activeEditable.contains(r.endContainer)) savedRange = r.cloneRange();
  }
  function restoreSelection() {
    if (!savedRange) return;
    try {
      const s = window.getSelection();
      s.removeAllRanges();
      s.addRange(savedRange);
    } catch (_) {}
  }

  function swatch(value, hex, kind) {
    if (value === "default") return `<button type="button" class="lf-swatch lf-none" data-color="default" data-kind="${kind}" title="기본"></button>`;
    return `<button type="button" class="lf-swatch" data-color="${value}" data-kind="${kind}" title="${value}" style="background:${hex}"></button>`;
  }
  function buildHtml() {
    const types = `<select class="lf-type" title="블록 유형">` + blockTypeDefs.map(([v, key, fb]) => `<option value="${v}">${BT[key] || fb}</option>`).join("") + `</select>`;
    const marks = MARK_BTNS.map((b) => `<button type="button" class="lf-btn" data-mark="${b.mark}" title="${b.title}">${b.icon}</button>`).join("");
    const aligns = ALIGN_BTNS.map((b) => `<button type="button" class="lf-btn" data-align="${b.align}" title="${b.title}">${b.icon}</button>`).join("");
    const presets = `<option value="default">기본</option>` + FS_PRESETS.map((s) => `<option value="${s}">${s}</option>`).join("");
    const fs = `<button type="button" class="lf-btn lf-fs-step" data-fs="-1" title="글자 작게">−</button><input type="text" class="lf-fs-input" inputmode="numeric" /><button type="button" class="lf-btn lf-fs-step" data-fs="1" title="글자 크게">+</button><select class="lf-fs-preset">${presets}</select>`;
    const colorBtns = `<button type="button" class="lf-btn lf-color-btn" data-pop="text" title="글자색">${ICON.textColor}</button><button type="button" class="lf-btn lf-color-btn" data-pop="bg" title="배경색">${ICON.bgColor}</button>`;
    const textPop = `<div class="lf-pop" data-for="text">` + TEXT_PALETTE.map(([v, h]) => swatch(v, h, "text")).join("") + `</div>`;
    const bgPop = `<div class="lf-pop" data-for="bg">` + BG_PALETTE.map(([v, h]) => swatch(v, h, "bg")).join("") + `</div>`;
    const nest = `<button type="button" class="lf-btn" data-act="outdent" title="내어쓰기">${ICON.outdent}</button><button type="button" class="lf-btn" data-act="indent" title="들여쓰기">${ICON.indent}</button>`;
    const link = onLink ? `<button type="button" class="lf-btn" data-act="link" title="링크 (Ctrl+K)">${ICON.link}</button>` : "";
    const sep = `<span class="lf-sep"></span>`;
    // 블록 전용 그룹은 lf-block 클래스로 감싸 표 셀 컨텍스트에선 CSS로 숨김
    return (
      `<div class="lf-group lf-block">${types}</div>${sep}` +
      `<div class="lf-group">${marks}</div>${sep}` +
      `<div class="lf-group lf-block">${aligns}</div><span class="lf-sep lf-block"></span>` +
      `<div class="lf-group">${fs}</div>${sep}` +
      `<div class="lf-group" style="position:relative">${colorBtns}${textPop}${bgPop}</div>` +
      `<span class="lf-sep lf-block"></span><div class="lf-group lf-block">${nest}${link}</div>`
    );
  }

  function closestEditable(node) {
    const el = node && node.nodeType === 1 ? node : node && node.parentElement;
    return el ? el.closest(".content, td.lumir-cell") : null;
  }
  function closePops() {
    bar.querySelectorAll(".lf-pop").forEach((p) => p.classList.remove("open"));
  }
  function hide() {
    bar.style.display = "none";
    closePops();
  }
  function position(rect) {
    bar.style.position = "absolute"; // 선택 근처 배치는 문서 좌표(absolute) 기준 — 전체선택 fixed 배치 후 복원
    // 수평 클램프 — 좁은 창에서 툴바가 뷰포트 밖으로 잘리지 않도록(우측/좌측 경계 안으로).
    const vw = (document.documentElement && document.documentElement.clientWidth) || window.innerWidth || 0;
    const vh = (document.documentElement && document.documentElement.clientHeight) || window.innerHeight || 0;
    const bw = bar.offsetWidth || 0;
    const bh = bar.offsetHeight || 0;
    let left = window.scrollX + (rect.left || 0);
    if (vw) {
      const maxLeft = window.scrollX + vw - bw - 8;
      if (left > maxLeft) left = maxLeft;
    }
    if (left < window.scrollX + 8) left = window.scrollX + 8;
    // 세로: 기본은 선택 위. 위 공간 없으면 선택 아래로 flip.
    let top = window.scrollY + rect.top - bh - 8;
    if (top < window.scrollY + 4) top = window.scrollY + (rect.bottom || rect.top || 0) + 8;
    // 여러 줄/전체 선택 등 큰 범위: 툴바가 뷰포트 밖(위·아래)으로 나가지 않도록 세로 클램프해 항상 보이게.
    if (vh) {
      const minTop = window.scrollY + 4;
      const maxTop = window.scrollY + vh - bh - 8;
      if (top > maxTop) top = maxTop;
      if (top < minTop) top = minTop;
    }
    bar.style.top = top + "px";
    bar.style.left = left + "px";
  }
  // Ctrl+A 전체 선택: 화면(뷰포트) 세로 1/4 지점에 고정(position:fixed)하되, 에디터 세로 범위
  // (최상단~최하단)를 벗어나지 않게 클램프한다. 스크롤해도(onScroll에서 재호출) 이 범위 안에 머문다.
  const SELECTALL_VIEWPORT_RATIO = 0.25; // 화면 세로 1/4 지점
  function positionForSelectAll() {
    const vw = (document.documentElement && document.documentElement.clientWidth) || window.innerWidth || 0;
    const vh = (document.documentElement && document.documentElement.clientHeight) || window.innerHeight || 0;
    const er = editorEl.getBoundingClientRect
      ? editorEl.getBoundingClientRect()
      : { left: 0, width: vw, top: 0, bottom: 0 };
    const bw = bar.offsetWidth || 0;
    const bh = bar.offsetHeight || 0;
    bar.style.position = "fixed"; // 뷰포트 기준 고정
    // 가로: 에디터 중앙(뷰포트 좌표) — 뷰포트 안으로 클램프
    let left = er.left + (er.width - bw) / 2;
    if (vw) {
      const maxLeft = vw - bw - 8;
      if (left > maxLeft) left = maxLeft;
    }
    if (left < 8) left = 8;
    bar.style.left = left + "px";
    // 세로: 화면 1/4 지점 지향, 단 에디터 최상단(er.top) 위 / 최하단(er.bottom) 아래로 안 나가게 클램프.
    const pad = 6;
    const lower = er.top + pad; // 에디터 최상위 경계
    const upper = er.bottom - bh - pad; // 에디터 최하위 경계
    let top = vh * SELECTALL_VIEWPORT_RATIO;
    if (top < lower) top = lower;
    if (top > upper) top = upper;
    bar.style.top = top + "px";
  }
  function isCell() {
    return !!(activeEditable && activeEditable.classList && activeEditable.classList.contains("lumir-cell"));
  }
  // 선택 지점의 계산된 font-size(px) — 명시적 fontSize 마크가 없을 때 실제 표시 크기를 보여주기 위함.
  function computedFsPx() {
    try {
      if (typeof getComputedStyle !== "function") return null;
      const s = window.getSelection();
      let node = s && s.rangeCount ? s.getRangeAt(0).startContainer : activeEditable;
      if (node && node.nodeType === 3) node = node.parentElement;
      if (!node || node.nodeType !== 1) node = activeEditable;
      if (!node) return null;
      const cs = getComputedStyle(node);
      const n = cs ? parseInt(cs.fontSize, 10) : NaN;
      return isNaN(n) ? null : n;
    } catch (_) { return null; }
  }
  function renderActive() {
    const crossActive = isCross();
    const marks = crossActive ? cross.readMarks() : readActiveMarks(activeEditable);
    MARK_BTNS.forEach((b) => {
      const el = bar.querySelector(`[data-mark="${b.mark}"]`);
      if (el) el.classList.toggle("on", !!marks[b.mark]);
    });
    // 명시적 fontSize가 있으면 그 값, 없으면 계산된 실제 크기(기본/제목 크기)를 표기.
    // 크로스 모드는 네이티브 선택이 비어 computedFsPx가 부정확하므로 명시값만 표기.
    const fsPx = marks.fontSize ? parseInt(marks.fontSize, 10) : (crossActive ? null : computedFsPx());
    const px = fsPx && !isNaN(fsPx) ? String(fsPx) : "";
    const input = bar.querySelector(".lf-fs-input");
    if (input) input.value = px;
    const preset = bar.querySelector(".lf-fs-preset");
    if (preset) preset.value = (fsPx && FS_PRESETS.includes(fsPx)) ? String(fsPx) : "default";
    // 블록 전용 도구: 표 셀·크로스 모드에선 숨김(크로스는 블록 타입/정렬/링크 미지원 — v1).
    bar.classList.toggle("lf-cell-ctx", !crossActive && isCell());
    bar.classList.toggle("lf-cross-ctx", crossActive);
    const b = activeBlock();
    if (!crossActive && b && !isCell()) {
      const sel = bar.querySelector(".lf-type");
      if (sel) sel.value = b.dataset.type === "heading" ? "heading:" + (b.dataset.level || "1") + (b.dataset.toggle === "true" ? ":toggle" : "") : b.dataset.type;
      const al = b.dataset.textAlignment || "left";
      ALIGN_BTNS.forEach((x) => { const el = bar.querySelector(`[data-align="${x.align}"]`); if (el) el.classList.toggle("on", al === x.align); });
    }
  }

  function update() {
    if (isComposing()) return;
    if (document.activeElement && bar.contains(document.activeElement)) return; // 툴바 입력 중엔 유지(숨김 금지)
    // 크로스블록 선택: 네이티브 range가 비어 있어 아래 경로는 hide 하므로 먼저 처리.
    if (isCross()) {
      if (cross.isDragging && cross.isDragging()) { hide(); return; } // 드래그 중엔 숨김(마우스업에 표시)
      if (suppress()) { hide(); return; } // 플로팅 메뉴 활성 시 팝업 억제(이중 툴바 방지)
      bar.style.display = "flex";
      renderActive();
      // Ctrl+A 전체 선택 → 화면 1/4 지점 고정(에디터 범위 클램프). 드래그 크로스 선택 → 선택 근처(유니온 rect).
      if (cross.isBlockSelect && cross.isBlockSelect()) {
        positionForSelectAll();
      } else {
        const rect = cross.boundingRect();
        if (!rect) { hide(); return; }
        position(rect);
      }
      return;
    }
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) { hide(); return; }
    const r = sel.getRangeAt(0);
    if (r.collapsed) { hide(); return; }
    const editable = closestEditable(r.startContainer);
    if (!editable || !editorEl.contains(editable) || !editorEl.contains(r.endContainer)) { hide(); return; }
    activeEditable = editable;
    savedRange = r.cloneRange(); // 표시 시점 선택 보존
    // FloatingMenu 활성 시 평문 선택은 억제(표 셀 선택은 유지)
    if (suppress() && !(editable.classList && editable.classList.contains("lumir-cell"))) { hide(); return; }
    bar.style.display = "flex";
    renderActive();
    const rect = r.getBoundingClientRect ? r.getBoundingClientRect() : { top: 0, left: 0 };
    position(rect);
  }

  function currentFs() {
    const marks = isCross() ? cross.readMarks() : readActiveMarks(activeEditable);
    if (marks.fontSize) return parseInt(marks.fontSize, 10);
    return computedFsPx() || FS_DEFAULT;
  }
  function applyFs(px) {
    const val = px == null ? "default" : Math.max(FS_MIN, Math.min(FS_MAX, px)) + "px";
    if (isCross()) {
      cross.applyMark("fontSize", val);
      renderActive();
      return;
    }
    applyInlineMark(activeEditable, "fontSize", val);
    onChange();
    renderActive();
    captureRange(); // innerHTML 교체로 무효화된 savedRange 갱신(연속 조작 대비)
  }

  // 선택 유지: 툴바 클릭 시 에디터 blur 방지(입력/셀렉트는 제외)
  bar.addEventListener("mousedown", (e) => {
    if (e.target.closest("input, select")) return;
    e.preventDefault();
  });

  bar.addEventListener("click", (e) => {
    const crossActive = isCross();
    if (!activeEditable && !crossActive) return;
    const markBtn = e.target.closest("[data-mark]");
    if (markBtn) {
      if (crossActive) cross.applyMark(markBtn.dataset.mark);
      else { applyInlineMark(activeEditable, markBtn.dataset.mark); onChange(); }
      renderActive();
      return;
    }
    const fsBtn = e.target.closest("[data-fs]");
    if (fsBtn) { applyFs(currentFs() + parseInt(fsBtn.dataset.fs, 10)); return; }
    const popBtn = e.target.closest("[data-pop]");
    if (popBtn) {
      const p = bar.querySelector(`.lf-pop[data-for="${popBtn.dataset.pop}"]`);
      const wasOpen = p.classList.contains("open");
      closePops();
      if (!wasOpen) p.classList.add("open");
      return;
    }
    const sw = e.target.closest(".lf-swatch");
    if (sw) {
      const kind = sw.dataset.kind === "text" ? "textColor" : "backgroundColor";
      if (crossActive) cross.applyMark(kind, sw.dataset.color);
      else { applyInlineMark(activeEditable, kind, sw.dataset.color); onChange(); }
      closePops();
      renderActive();
      return;
    }
    // 블록 전용 도구(정렬/타입/들여쓰기/링크)는 크로스 모드에서 미지원(v1) — 무시.
    if (crossActive) return;
    const alignBtn = e.target.closest("[data-align]");
    if (alignBtn) {
      const b = activeBlock();
      if (b) { setBlockAlignment(b, alignBtn.dataset.align); onChange(); renderActive(); }
      return;
    }
    const actBtn = e.target.closest("[data-act]");
    if (actBtn) {
      const a = actBtn.dataset.act;
      const b = activeBlock();
      if (a === "link") { if (onLink) onLink(); return; }
      if (a === "indent" && b) { if (indentBlock(b)) { activeEditable = directContent(b); onChange(); } return; }
      if (a === "outdent" && b) { if (outdentBlock(b)) { activeEditable = directContent(b); onChange(); } return; }
      return;
    }
  });

  bar.addEventListener("change", (e) => {
    const crossActive = isCross();
    if (!activeEditable && !crossActive) return;
    if (e.target.classList.contains("lf-type")) {
      if (crossActive) return; // 크로스 모드는 블록 타입 변경 미지원(v1)
      const b = activeBlock();
      if (!b) return;
      const [type, lvl, flag] = e.target.value.split(":");
      const el = setBlockType(b, type, lvl ? parseInt(lvl, 10) : undefined, flag === "toggle");
      activeEditable = directContent(el);
      onChange();
      renderActive();
    } else if (e.target.classList.contains("lf-fs-preset")) {
      const v = e.target.value;
      if (!crossActive) restoreSelection(); // 셀렉트 포커스로 잃은 선택 복원 후 적용(크로스는 오프셋 기반이라 불요)
      applyFs(v === "default" ? null : parseInt(v, 10));
    } else if (e.target.classList.contains("lf-fs-input")) {
      const n = parseInt(e.target.value, 10);
      if (!crossActive) restoreSelection();
      if (!isNaN(n)) applyFs(n);
    }
  });

  // 글자크기 입력칸: Enter 적용 / ArrowUp·Down ±1px (원본 FontSizeButton 패리티)
  bar.addEventListener("keydown", (e) => {
    const crossActive = isCross();
    if (!activeEditable && !crossActive) return;
    const input = e.target;
    if (!input.classList || !input.classList.contains("lf-fs-input")) return;
    if (e.key === "Enter") {
      e.preventDefault();
      const n = parseInt(input.value, 10);
      if (!crossActive) restoreSelection();
      if (!isNaN(n)) applyFs(n);
      input.focus();
    } else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      if (!crossActive) restoreSelection();
      applyFs(currentFs() + (e.key === "ArrowUp" ? 1 : -1));
      input.focus();
    }
  });

  let raf = 0;
  function onSel() {
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(update);
  }
  // Ctrl+A 전체 선택(fixed 배치)일 때만 스크롤 중 재클램프 — 에디터 범위 밖으로 나가지 않게.
  let scrollRaf = 0;
  function onScroll() {
    if (!(cross && cross.isBlockSelect && cross.isBlockSelect())) return;
    if (bar.style.display === "none") return;
    if (scrollRaf) cancelAnimationFrame(scrollRaf);
    scrollRaf = requestAnimationFrame(positionForSelectAll);
  }
  document.addEventListener("selectionchange", onSel);
  editorEl.addEventListener("mouseup", onSel);
  editorEl.addEventListener("keyup", onSel);
  window.addEventListener("scroll", onScroll, true); // capture: 스크롤 컨테이너/윈도우 모두 포착

  return {
    element: bar,
    update,
    hide,
    destroy() {
      document.removeEventListener("selectionchange", onSel);
      editorEl.removeEventListener("mouseup", onSel);
      editorEl.removeEventListener("keyup", onSel);
      window.removeEventListener("scroll", onScroll, true);
      if (scrollRaf) cancelAnimationFrame(scrollRaf);
      if (bar.parentNode) bar.parentNode.removeChild(bar);
    },
  };
}
